import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatosPorDia } from '../../services/gestionService';
import { createDateFromISOString } from '../../utils/dateUtils';

interface GraficoCalendarioProps {
  datos: DatosPorDia[];
  isLoading: boolean;
  horasDisponibles?: number;
}

interface DiaDetalle {
  fecha: string;
  maquinas: Array<{
    codigo: string;
    id: number;
    horas: number;
  }>;
}

type VistaCalendario = 'calendario' | 'gantt';

export const GraficoCalendario: React.FC<GraficoCalendarioProps> = ({
  datos,
  isLoading,
  horasDisponibles = 24,
}) => {
  const { t } = useTranslation();
  const [vista, setVista] = useState<VistaCalendario>('calendario');
  const [diasMap, setDiasMap] = useState<Map<string, DiaDetalle>>(new Map());
  const [maquinas, setMaquinas] = useState<string[]>([]);
  const [mesActual, setMesActual] = useState(new Date());
  const [diaSeleccionado, setDiaSeleccionado] = useState<string | null>(null);

  useEffect(() => {
    if (datos && datos.length > 0) {
      // Agrupar datos por fecha
      const mapa = new Map<string, DiaDetalle>();
      const maquinasSet = new Set<string>();

      datos.forEach((item) => {
        const fecha = item.fecha;
        if (!mapa.has(fecha)) {
          mapa.set(fecha, { fecha, maquinas: [] });
        }
        mapa.get(fecha)!.maquinas.push({
          codigo: item.maquina_codigo,
          id: item.maquina_id,
          horas: item.horas,
        });
        maquinasSet.add(item.maquina_codigo);
      });

      setDiasMap(mapa);
      setMaquinas(Array.from(maquinasSet).sort());

      // Establecer el mes del primer dato si existe
      if (datos.length > 0) {
        const [year, month, day] = datos[0].fecha.split('-').map(Number);
        setMesActual(new Date(year, month - 1, day));
      }
    }
  }, [datos]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (diasMap.size === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.no_data')}</div>
      </div>
    );
  }

  const handleMesAnterior = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1));
    setDiaSeleccionado(null);
  };

  const handleMesSiguiente = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1));
    setDiaSeleccionado(null);
  };

  return (
    <div className="w-full space-y-4">
      {/* Toggle de vistas */}
      <div className="flex gap-2 border-b border-gray-200 pb-4">
        <button
          onClick={() => setVista('calendario')}
          className={`px-4 py-2 rounded font-medium transition-colors ${
            vista === 'calendario'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {t('management.calendar')}
        </button>
        <button
          onClick={() => setVista('gantt')}
          className={`px-4 py-2 rounded font-medium transition-colors ${
            vista === 'gantt'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 5a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2H9z" />
          </svg>
          Gantt
        </button>
      </div>

      {/* Navegador de mes */}
      <div className="flex items-center justify-center gap-4 bg-white rounded-lg shadow p-4">
        <button
          onClick={handleMesAnterior}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Mes anterior"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
          {mesActual.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
        </div>

        <button
          onClick={handleMesSiguiente}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Mes siguiente"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Vista Calendario */}
      {vista === 'calendario' && (
        <CalendarioGrid
          mesActual={mesActual}
          diasMap={diasMap}
          diaSeleccionadoStr={diaSeleccionado}
          onSelectDia={setDiaSeleccionado}
        />
      )}

      {/* Vista Gantt */}
      {vista === 'gantt' && (
        <GanttChart
          mesActual={mesActual}
          diasMap={diasMap}
          maquinas={maquinas}
          horasDisponibles={horasDisponibles}
        />
      )}

      {/* Modal de detalle de día */}
      {diaSeleccionado && vista === 'calendario' && (
        <ModalDetalleDia
          diaStr={diaSeleccionado}
          diasMap={diasMap}
          onClose={() => setDiaSeleccionado(null)}
        />
      )}
    </div>
  );
};

// ====================================
// VISTA CALENDARIO - GRID TRADICIONAL
// ====================================

interface CalendarioGridProps {
  mesActual: Date;
  diasMap: Map<string, DiaDetalle>;
  diaSeleccionadoStr: string | null;
  onSelectDia: (fecha: string | null) => void;
}

const CalendarioGrid: React.FC<CalendarioGridProps> = ({
  mesActual,
  diasMap,
  diaSeleccionadoStr,
  onSelectDia,
}) => {
  const { t } = useTranslation();
  const year = mesActual.getFullYear();
  const month = mesActual.getMonth();

  // Primer día del mes
  const primerDia = new Date(year, month, 1);
  const primerDiaSemana = primerDia.getDay();

  // Último día del mes
  const ultimoDia = new Date(year, month + 1, 0).getDate();

  // Array de días (null para días del mes anterior)
  const dias: (number | null)[] = [
    ...Array(primerDiaSemana).fill(null),
    ...Array.from({ length: ultimoDia }, (_, i) => i + 1),
  ];

  const nombreDias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sab'];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Encabezado de días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {nombreDias.map((dia) => (
          <div key={dia} className="text-center text-sm font-semibold text-gray-700 py-2">
            {dia}
          </div>
        ))}
      </div>

      {/* Grid de días */}
      <div className="grid grid-cols-7 gap-1">
        {dias.map((dia, index) => {
          if (dia === null) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const diaData = diasMap.get(fechaStr);
          const tieneDatos = !!diaData;
          const isSelected = diaSeleccionadoStr === fechaStr;

          return (
            <button
              key={dia}
              onClick={() => {
                if (tieneDatos) {
                  if (isSelected) {
                    onSelectDia(null);
                  } else {
                    onSelectDia(fechaStr);
                  }
                }
              }}
              className={`aspect-square p-2 rounded-lg border-2 transition-all text-sm font-medium ${
                tieneDatos
                  ? isSelected
                    ? 'border-blue-600 bg-blue-50 cursor-pointer'
                    : 'border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100 cursor-pointer'
                  : 'border-gray-200 bg-white text-gray-400 cursor-default'
              }`}
            >
              <div className="text-center">{dia}</div>
              {tieneDatos && (
                <div className="text-xs text-gray-600 mt-1">
                  {diaData.maquinas.length} máq.
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-6 flex gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-50 border-2 border-green-300 rounded" />
          <span className="text-gray-600">{t('common.active')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border-2 border-gray-200 rounded" />
          <span className="text-gray-600">{t('common.inactive')}</span>
        </div>
      </div>
    </div>
  );
};

// ====================================
// VISTA GANTT - HEATMAP DE MÁQUINAS
// ====================================

interface GanttChartProps {
  mesActual: Date;
  diasMap: Map<string, DiaDetalle>;
  maquinas: string[];
  horasDisponibles: number;
}

const GanttChart: React.FC<GanttChartProps> = ({
  mesActual,
  diasMap,
  maquinas,
  horasDisponibles,
}) => {
  const year = mesActual.getFullYear();
  const month = mesActual.getMonth();
  const ultimoDia = new Date(year, month + 1, 0).getDate();

  const getColorForHoras = (horas: number, disponibles: number): string => {
    const porcentaje = (horas / disponibles) * 100;

    if (porcentaje === 0) return '#f3f4f6'; // gris claro
    if (porcentaje < 25) return '#dcfce7'; // verde muy claro
    if (porcentaje < 50) return '#86efac'; // verde claro
    if (porcentaje < 75) return '#4ade80'; // verde
    if (porcentaje < 100) return '#22c55e'; // verde oscuro
    return '#16a34a'; // verde muy oscuro (>100%)
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
      <div className="flex">
        {/* Columna de máquinas */}
        <div className="flex flex-col">
          <div className="h-10 bg-gray-100 border-b border-r border-gray-300 flex items-center px-3 font-semibold text-sm text-gray-700 w-32">
            Máquinas
          </div>
          {maquinas.map((maquina) => (
            <div
              key={maquina}
              className="h-10 bg-white border-b border-r border-gray-300 flex items-center px-3 text-sm font-medium text-gray-900 w-32"
            >
              {maquina}
            </div>
          ))}
        </div>

        {/* Grid de días */}
        <div className="flex">
          {/* Encabezado de días */}
          <div className="flex flex-col">
            <div className="flex">
              {Array.from({ length: ultimoDia }, (_, i) => i + 1).map((dia) => (
                <div
                  key={`header-${dia}`}
                  className="h-10 w-8 bg-gray-100 border-b border-r border-gray-300 flex items-center justify-center font-semibold text-xs text-gray-700"
                >
                  {dia}
                </div>
              ))}
            </div>

            {/* Celdas de datos */}
            {maquinas.map((maquina) => (
              <div key={maquina} className="flex">
                {Array.from({ length: ultimoDia }, (_, i) => i + 1).map((dia) => {
                  const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                  const diaData = diasMap.get(fechaStr);
                  const maquinaData = diaData?.maquinas.find((m) => m.codigo === maquina);
                  const horas = maquinaData?.horas || 0;

                  return (
                    <div
                      key={`${maquina}-${dia}`}
                      className="h-10 w-8 border-b border-r border-gray-300 flex items-center justify-center cursor-pointer group relative"
                      style={{ backgroundColor: getColorForHoras(horas, horasDisponibles) }}
                      title={horas > 0 ? `${maquina} - ${horas}h` : ''}
                    >
                      {horas > 0 && (
                        <span className="text-xs font-bold text-gray-900">{horas}</span>
                      )}
                      {/* Tooltip */}
                      {horas > 0 && (
                        <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                          {maquina}: {horas}h / {horasDisponibles}h
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-gray-900 border-t-gray-900 border-r-transparent border-b-transparent border-l-transparent" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leyenda de colores */}
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 rounded" />
          <span className="text-gray-600">0%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 rounded" />
          <span className="text-gray-600">{'<25%'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-300 rounded" />
          <span className="text-gray-600">25-50%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded" />
          <span className="text-gray-600">50-100%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-700 rounded" />
          <span className="text-gray-600">{'>100%'}</span>
        </div>
      </div>
    </div>
  );
};

// ====================================
// MODAL DE DETALLE DE DÍA
// ====================================

interface ModalDetalleDiaProps {
  diaStr: string;
  diasMap: Map<string, DiaDetalle>;
  onClose: () => void;
}

const ModalDetalleDia: React.FC<ModalDetalleDiaProps> = ({ diaStr, diasMap, onClose }) => {
  const { t } = useTranslation();
  const diaData = diasMap.get(diaStr);

  if (!diaData) {
    return null;
  }

  const fecha = createDateFromISOString(diaStr);
  const fechaFormato = fecha.toLocaleDateString('es-CL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-4 border-l-4 border-blue-600">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">{t('common.details')}</h4>
          <p className="text-sm text-gray-600 mt-1 capitalize">{fechaFormato}</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {diaData.maquinas.map((maquina) => (
          <div key={maquina.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="font-medium text-gray-900">{maquina.codigo}</span>
            <span className="text-blue-600 font-semibold">{maquina.horas}h</span>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Total: <span className="font-semibold text-gray-900">
            {diaData.maquinas.reduce((sum, m) => sum + m.horas, 0)}h
          </span>
        </p>
      </div>
    </div>
  );
};
